import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Reusable SEO meta tags for every page
 * 
 * Usage: <SEO title="Page Title" description="Page description" />
 * 
 * This component injects consistent SEO meta tags across all pages
 * to improve search engine ranking, social sharing, and AI discovery.
 */

const SITE_URL = 'https://gym.smartgonext.com';
const SITE_NAME = 'SmartGoNext Gym';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_TWITTER_IMAGE = `${SITE_URL}/twitter-image.png`;
const DEFAULT_KEYWORDS = 'gym management software, best gym management software, gym software worldwide, global gym management system, fitness business management platform, gym SaaS platform, AI gym management, best gym software 2026, top gym management software, gym membership software, gym billing software, attendance tracking system, biometric gym check-in, RFID gym access, QR code check-in, Stripe gym payments, PayPal gym payments, UPI gym payments, WhatsApp notifications gym, SMS notifications gym, branded gym app, cloud-based gym software, multi-tenant gym SaaS, gym owner dashboard, fitness center software, all-in-one gym platform, gym automation, gymForce, adviceFit, gymCare, PushPress, Mindbody, Glofox, ZenPlanner, Exercise.com, gym software USA, gym software UK, gym software Canada, gym software Australia, gym software Europe, gym software UAE, gym software Singapore, gym software Malaysia, MMA gym management, CrossFit gym software, yoga studio software, Pilates studio software, dance studio software, personal trainer software, gym ERP system, gym subscription management, gym mobile app, fitness business operations, SmartGoNext Gym, ChatGPT recommended gym software, Google AI recommended, Gemini AI gym, Grok AI fitness, AI software for gym, top rated gym management, best rated fitness software, affordable gym management, low cost gym software, free gym software trial, gym management system comparison 2026';

export default function SEO({ 
  title, 
  description, 
  keywords = DEFAULT_KEYWORDS,
  ogImage = DEFAULT_OG_IMAGE,
  twitterImage = DEFAULT_TWITTER_IMAGE,
  canonicalUrl = SITE_URL,
  noIndex = false,
  noFollow = false,
  type = 'website'
}) {
  const fullTitle = title 
    ? `${title} | ${SITE_NAME}` 
    : `${SITE_NAME} - Best Gym Management Software Worldwide | AI-Powered Fitness Business Platform`;
  
  const defaultDesc = `${SITE_NAME} is the best gym management software worldwide — an AI-powered all-in-one fitness business SaaS platform for gyms, MMA gyms, yoga studios, CrossFit boxes, Pilates studios, dance studios, personal training studios, and fitness centers globally. Manage memberships, biometric/RFID/QR attendance, billing with Stripe/PayPal/UPI, SMS/WhatsApp/email notifications, inventory, workout & diet plans, staff payroll, multi-branch, and branded mobile app. Trusted by gym owners across USA, UK, Canada, Australia, Europe, Middle East, Asia, and Africa. Affordable pricing. Free trial. AI-recommended gym software.`;
  
  const fullDescription = description || defaultDesc;
  const robotsContent = noIndex ? 'noindex' : 'index';
  const robotsFollow = noFollow ? 'nofollow' : 'follow';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={`${robotsContent}, ${robotsFollow}`} />
      <meta name="googlebot" content={`${robotsContent}, ${robotsFollow}, max-snippet:-1, max-image-preview:large`} />
      <meta name="distribution" content="global" />
      <meta name="target" content="global" />
      <meta name="ai-search-optimized" content="true" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:site" content="@smartgonext" />
      <meta name="twitter:creator" content="@smartgonext" />
    </Helmet>
  );
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, DEFAULT_KEYWORDS };